// Tiny shared signal between two sibling overlays that both sit at the bottom of
// the viewport: the concierge chat panel and the cookie-consent banner.
//
// Problem it solves: on mobile both are `fixed bottom-0`. When the chat panel is
// open it must sit ABOVE the cookie banner, and the banner must step out of the
// way entirely so it can't intercept taps on the consent checkbox / Start button
// (a first-visit mobile user otherwise cannot start the chat).
//
// ConciergeChat is the single writer (`setChatOpen`); CookieConsentBanner is a
// reader (`useChatOpen`) that hides itself while the chat is open. No heavy dep -
// just a module-level boolean + a `useSyncExternalStore` subscription, the same
// vendor-agnostic store shape as `lib/consent`.

import { useSyncExternalStore } from 'react'

type Listener = () => void

let chatOpen = false
const listeners = new Set<Listener>()

/** Writer - ConciergeChat calls this as its `open` state changes. */
export function setChatOpen(value: boolean): void {
  if (chatOpen === value) return
  chatOpen = value
  for (const fn of listeners) fn()
}

function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

function getSnapshot(): boolean {
  return chatOpen
}

// The server render is always "chat closed" (the chat is a client-only widget),
// so the banner renders normally during SSR/first paint. Not reached under the
// jsdom client render the unit tests use, hence the v8 ignore.
/* v8 ignore start */
function getServerSnapshot(): boolean {
  return false
}
/* v8 ignore stop */

/** Reader hook - re-renders the caller whenever the chat opens or closes. */
export function useChatOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/** Test-only: reset the module-level flag + listeners between tests. */
export function _resetChatPresenceForTesting(): void {
  chatOpen = false
  listeners.clear()
}
